import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import useDomain from '@/hooks/useDomain';
import { toolRoutes } from '@/routes/siteMaps';
import RouteHelperAlerts from '@/components/common/RouteHelperAlerts';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import ToolTabs from '@/app/common/components/ToolTabs';
import SubtleBadge from '@/components/common/SubtleBadge';
import { ProjectProvider, useProject } from './context/ProjectContext';
import { BrandLogoProvider } from './context/BrandLogoContext';
import {
  NewPromptModal,
  NewTopicModal
} from './components/Modals';
import { ProjectSetupProvider, useProjectSetup } from './hooks/useProjectSetupOnboarding';

type BreadcrumbItem = { name: string; to?: string };

type ToolRouteChild = {
  name: string;
  to: string;
  helper?: React.ReactNode;
  badge?: { type?: string; text?: string };
};

function TrackerAiShell() {
  useEffect(() => {
    document.body.classList.add('tracker-ai-route');
    return () => document.body.classList.remove('tracker-ai-route');
  }, []);

  const { domain, domain_slug } = useDomain();
  const domainSuffix =
    domain_slug && domain ? `/${domain_slug}/${domain}` : '';
  const routes = toolRoutes.children.find(item => item.name === 'ردیاب AI');
  const location = useLocation();
  const setup = useProjectSetup();
  const onOnboarding = location.pathname.includes('/tracker-ai/onboarding/');
  const [breadcrumbData, setBreadcrumbData] = useState<BreadcrumbItem[]>([]);
  const navItems = routes;
  const {
    newPromptOpen,
    setNewPromptOpen,
    newTopicOpen,
    setNewTopicOpen,
    creatingPrompt,
    creatingTopic,
    handleCreatePrompt,
    handleCreateTopic,
    completeTopicSetup,
    models,
    defaultModelId,
    topics,
    topicFilterId,
    newPromptTopicId
  } = useProject();

  const activePage = useMemo(
    () =>
      navItems?.children?.find((route): route is ToolRouteChild => {
        if (typeof route.to !== 'string') return false;
        const to = `${route.to}${domainSuffix}`;
        return location.pathname === to || location.pathname.startsWith(`${to}/`);
      }),
    [navItems, domainSuffix, location.pathname]
  );

  return (
    <div className="tracker-ai">
      <ToolsBreadcrumb
        parentName="ردیاب AI"
        parentUrl={`/tracker-ai/overview${domainSuffix}`}
        childs={
          onOnboarding
            ? [{ text: 'راه‌اندازی', href: setup.onboardingPath }]
            : breadcrumbData
        }
      />
      {!onOnboarding && (
        <ToolTabs
          items={(navItems?.children ?? [])
            .filter((route): route is ToolRouteChild => typeof route.to === 'string')
            .map(route => {
            const to = `${route.to}${domainSuffix}`;
            return {
              key: route.to,
              label: route.name,
              to,
              active:
                location.pathname === to ||
                location.pathname.startsWith(`${to}/`),
              badge: route.badge?.text ? (
                <SubtleBadge bg={route.badge.type || 'success'} className="ms-1">
                  {route.badge.text}
                </SubtleBadge>
              ) : undefined
            };
          })}
        />
      )}
      {!onOnboarding && activePage && (
        <RouteHelperAlerts route={activePage} className="mt-2 mb-4" />
      )}
      <Outlet context={[breadcrumbData, setBreadcrumbData]} />
      <NewPromptModal
        open={newPromptOpen}
        onClose={() => setNewPromptOpen(false)}
        topics={topics}
        models={models}
        defaultModelId={defaultModelId}
        defaultTopicId={newPromptTopicId ?? topicFilterId}
        onSubmit={handleCreatePrompt}
        saving={creatingPrompt}
      />
      <NewTopicModal
        open={newTopicOpen}
        onClose={() => setNewTopicOpen(false)}
        models={models}
        defaultModelId={defaultModelId}
        onCreateTopic={handleCreateTopic}
        onComplete={completeTopicSetup}
        saving={creatingTopic}
      />
    </div>
  );
}

export default function TrackerAiLayout() {
  return (
    <ProjectProvider>
      <BrandLogoProvider>
        <ProjectSetupProvider>
          <TrackerAiShell />
        </ProjectSetupProvider>
      </BrandLogoProvider>
    </ProjectProvider>
  );
}
